import { useEffect, useState } from 'react';
import adminAxios from '../lib/adminAxios';
import { Loader2, Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentsPanel() {
  const [cfg, setCfg] = useState(null);
  const [tx, setTx] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      const [c, t] = await Promise.all([
        adminAxios.get(`${API}/payments/config`),
        adminAxios.get(`${API}/admin/payments/transactions`),
      ]);
      setCfg(c.data);
      setTx(t.data);
    } catch {
      setCfg({ deposit_amount: 250, currency: 'gbp' });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    setResult(null);
    try {
      await adminAxios.put(`${API}/admin/payments/config`, {
        deposit_amount: Number(cfg.deposit_amount),
        currency: cfg.currency,
      });
      setResult({ ok: true, msg: 'Saved.' });
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24" data-testid="admin-payments-panel">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="eyebrow text-gold mb-4">Payments</div>
          <h2 className="font-display text-3xl md:text-4xl text-mb">Stripe · reservations & orders.</h2>
        </div>
        <div className="eyebrow-sm text-mb-muted">Test mode</div>
      </div>

      <div className="bg-mb-surface hairline border-subtle p-6 md:p-8 mb-8">
        <div className="eyebrow text-mb-muted mb-6">Reservation deposit</div>
        <div className="grid grid-cols-[1fr_100px_auto_auto] gap-3 items-end">
          <div>
            <label className="mb-label">Amount</label>
            <input
              type="number"
              className="mb-input"
              value={cfg.deposit_amount}
              onChange={(e) => setCfg({ ...cfg, deposit_amount: e.target.value })}
              data-testid="admin-deposit-amount"
            />
          </div>
          <div>
            <label className="mb-label">Currency</label>
            <select
              className="mb-input"
              value={cfg.currency}
              onChange={(e) => setCfg({ ...cfg, currency: e.target.value })}
              data-testid="admin-deposit-currency"
              style={{ appearance: 'none' }}
            >
              {['gbp', 'usd', 'eur'].map((c) => (
                <option key={c} value={c} style={{ background: '#2a0d1a', color: '#F0EDE5' }}>{c.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <button className="btn-gold disabled:opacity-50" onClick={save} disabled={busy} data-testid="admin-save-deposit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
          </button>
          {result && (
            <div className={`eyebrow-sm ${result.ok ? 'text-gold' : 'text-mb-muted'} flex items-center gap-2`}>
              {result.ok ? <Check size={12} /> : <AlertCircle size={12} />} {result.msg}
            </div>
          )}
        </div>
      </div>

      <div className="bg-mb-surface hairline border-subtle p-6 md:p-8">
        <div className="eyebrow text-mb-muted mb-6">Recent transactions ({tx.length})</div>
        {tx.length === 0 ? (
          <p className="text-[13px] text-mb-muted">No payments yet.</p>
        ) : (
          <div className="hairline border-subtle">
            <div className="grid grid-cols-[100px_1fr_100px_120px_120px] gap-3 items-center px-3 py-2 hairline-b border-subtle eyebrow-sm text-mb-muted">
              <div>Kind</div><div>Item</div><div>Amount</div><div>Status</div><div>Created</div>
            </div>
            {tx.map((t) => (
              <div key={t.session_id} className="grid grid-cols-[100px_1fr_100px_120px_120px] gap-3 items-center px-3 py-2 hairline-b border-subtle">
                <div className="eyebrow-sm text-gold">{t.kind}</div>
                <div className="font-display text-[14px] text-mb truncate">{t.display_name || t.target_id}</div>
                <div className="font-display text-[14px] text-mb">{t.currency?.toUpperCase()} {Number(t.amount).toFixed(2)}</div>
                <div className={`eyebrow-sm ${t.payment_status === 'paid' ? 'text-gold' : 'text-mb-muted'}`}>{t.payment_status}</div>
                <div className="eyebrow-sm text-mb-muted">{new Date(t.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
